import { StationsInputMui } from '@app/components/stations/StationsInputMui';
import { useToast } from '@app/core/notifications';
import { isVideoStation } from '@app/core/stations/helpers';
import { useOwnedStations } from '@app/core/stations/hooks/useOwnedStations';
import { zodResolver } from '@hookform/resolvers/zod';
import { videoAnalysis } from '@mi-front/api-clients';
import { Tokenizer } from '@mi-front/core';
import { ChipsField, QueryView } from '@mi-front/ui';
import { Alert, Box, Card, CardContent, CardHeader, Grid, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FormContainer } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { FormActionsMui } from '../../../../components/form/actions/FormActionsMui';

const schema = z.object({
  adOverlays: z.object({
    ignoredEntities: z.array(z.string()),
    numericAllowlist: z.array(z.string()),
  }),
});

export function VideoAnalysisForm() {
  const queryClient = useQueryClient();
  const stations = useOwnedStations(isVideoStation);
  const [station, setStation] = useState(stations[0]);
  const { $t } = useIntl();
  const { notifySuccess } = useToast();

  const query = videoAnalysis.useStationSettingsClientControllerGetSettings(station.id, {
    query: {
      enabled: !!station.id,
    },
  });
  const mutation = videoAnalysis.useStationSettingsClientControllerPatchSettings();

  const tokenizer = new Tokenizer();

  return (
    <Box>
      <Box mb={3} maxWidth={400}>
        <StationsInputMui stations={stations} value={station} onChange={setStation} />
      </Box>

      <QueryView query={query}>
        {(data) => {
          return (
            <Box>
              <FormContainer
                key={station.id}
                defaultValues={{
                  adOverlays: {
                    ignoredEntities: tokenizer.split(data.settings.adOverlays?.ignoredEntities),
                    numericAllowlist: tokenizer.split(data.settings.adOverlays?.numericAllowlist),
                  },
                }}
                resolver={zodResolver(schema)}
                onSuccess={async (data) => {
                  await mutation.mutateAsync({
                    entityId: station.id,
                    data: {
                      adOverlays: {
                        ignoredEntities: tokenizer.join(data.adOverlays?.ignoredEntities) ?? null,
                        numericAllowlist: tokenizer.join(data.adOverlays?.numericAllowlist) ?? null,
                      },
                    },
                  });

                  queryClient.invalidateQueries({
                    queryKey: videoAnalysis.stationSettingsClientControllerGetSettingsQueryKey(
                      station.id,
                    ),
                  });

                  notifySuccess(
                    $t({
                      id: 'admin.videoAnalysis.settingsUpdated',
                      defaultMessage: 'Configuración actualizada',
                    }),
                  );
                }}
              >
                <Card>
                  <CardHeader
                    title={
                      <Typography variant="h6" component="h2">
                        {$t({
                          id: 'admin.videoAnalysis.adOverlays.title',
                          defaultMessage: 'Ad Overlays',
                        })}
                      </Typography>
                    }
                  />
                  <CardContent>
                    {!data.settings.adOverlays?.enabled && (
                      <Alert severity="warning">
                        {$t({
                          id: 'admin.videoAnalysis.adOverlays.notEnabledForStation',
                          defaultMessage: 'Ad Overlays no activado para esta emisora',
                        })}
                      </Alert>
                    )}
                    {data.settings.adOverlays?.enabled && (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <ChipsField
                            name="adOverlays.ignoredEntities"
                            label={$t({
                              id: 'admin.videoAnalysis.adOverlays.ignoredEntities',
                              defaultMessage: 'Entidades ignoradas',
                            })}
                            separator={[',']}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <ChipsField
                            name="adOverlays.numericAllowlist"
                            label={$t({
                              id: 'admin.videoAnalysis.adOverlays.numericAllowlist',
                              defaultMessage: 'Lista numérica permitida',
                            })}
                            validateOption={(option) => {
                              return !Number.isNaN(Number(option));
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </CardContent>
                </Card>

                <FormActionsMui />
              </FormContainer>
            </Box>
          );
        }}
      </QueryView>
    </Box>
  );
}
